import { useState } from 'react';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { bdErrorMessage, isForbidden } from '../bdErrors';
import { formatDate, formatMoney } from '../dates';
import { isActiveEmployee } from '../employeeDirectory';
import { deductionsGateway } from '../index';
import { useEmployeeDirectory } from '../useEmployeeDirectory';
import type { GarnishmentRecord } from '../types';
import { SelectInput } from './fields';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

type Load = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; rows: GarnishmentRecord[] } | { kind: 'forbidden' } | { kind: 'error'; message: string };

/**
 * The existing garnishment records for one employee (read only). Phase 1 adds no garnishment limit, priority or CCPA logic: a record here is shown exactly as
 * stored, and the page says that applicable rules control limits and priorities and that rule review is required. Records are listed only after an
 * employee is chosen, and nothing here is written to a log.
 */
export function GarnishmentRecords() {
  const { state: directory } = useEmployeeDirectory();
  const [employeeId, setEmployeeId] = useState('');
  const [load, setLoad] = useState<Load>({ kind: 'idle' });

  async function show(id: string) {
    setEmployeeId(id);
    if (!id) { setLoad({ kind: 'idle' }); return; }
    setLoad({ kind: 'loading' });
    try {
      setLoad({ kind: 'ready', rows: await deductionsGateway.listGarnishments(id) });
    } catch (err) {
      setLoad(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: bdErrorMessage(err, 'Could not load the garnishment records.') });
    }
  }

  const employees = directory.kind === 'ready' ? directory.employees.filter(isActiveEmployee) : [];
  return (
    <div className="bd-panel" data-testid="garnishment-records">
      <h3>Existing garnishment records</h3>
      <p className="bd-help">
        These are the records already on file. Rule review required: EPN does not calculate garnishment limits or priorities here. Choose an employee to see their records.
      </p>
      <SelectInput
        id="bd-garn-employee" label="Employee" value={employeeId} onChange={(v) => void show(v)}
        options={[{ value: '', label: directory.kind === 'loading' ? 'Loading employees…' : 'Choose an employee' }, ...employees.map((e) => ({ value: e.id, label: `${e.name} (${e.employeeNumber})` }))]}
      />
      {load.kind === 'loading' && <p className="muted" role="status">Loading records{'…'}</p>}
      {load.kind === 'error' && <RetryableError message={load.message} onRetry={() => void show(employeeId)} />}
      {load.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view garnishments (garnishments.read)" />}
      {load.kind === 'ready' && load.rows.length === 0 && <p className="muted" data-testid="garnishment-empty">No garnishment records for this employee.</p>}
      {load.kind === 'ready' && load.rows.length > 0 && (
        <div className="jr-table-scroll" role="region" aria-label="Garnishment records table" tabIndex={0}>
          <table className="jr-table">
            <caption className="jr-sr-only">Garnishment records for the chosen employee</caption>
            <thead>
              <tr><th scope="col">Type</th><th scope="col">Case number</th><th scope="col">Priority</th><th scope="col">Amount</th><th scope="col">Issued by</th><th scope="col">Effective</th></tr>
            </thead>
            <tbody>
              {load.rows.map((r) => (
                <tr key={r.id}>
                  <th scope="row">{r.typeLabel}</th>
                  <td>{r.caseNumber}</td>
                  <td>{r.priority}</td>
                  <td>{r.amountTypeLabel === 'Flat amount' ? formatMoney(r.amount) : `${r.amount}%`} <span className="bd-table-note">{r.amountTypeLabel}</span></td>
                  <td>{r.issuingAuthority ?? <span className="muted">{'—'}</span>}</td>
                  <td className="jr-nowrap">{formatDate(r.effectiveStartDate)}{r.effectiveEndDate ? ` to ${formatDate(r.effectiveEndDate)}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
